import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import styles from './RecruiterAnalytics.module.css';
import Select from 'react-select';
import { executivesData, useExecutives, getExecutiveFullName } from '../../store/ExecutiveContext';

import { DateRangePicker } from 'react-date-range';
import { enUS } from 'date-fns/locale';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { BASE_URL_API } from '../../References/urls';
import AuthContext from '../../store/auth-context';
import { safeParseArray } from '../../Helper/helpers';

const RecruiterAnalytics = () => {
    const pickerRef = useRef(null);
    const authCtx = useContext(AuthContext);
    const hasAccessTo = safeParseArray(authCtx?.hasAccessTo);
    const optionsExecutive =
        authCtx?.userIdentifier === "999964N8406"
            ? executivesData?.map(e => ({
                value: e?.userID,
                label: getExecutiveFullName(e?.userID),
            })) ?? []
            : [
                ...(authCtx?.userIdentifier
                    ? [{
                        value: authCtx.userIdentifier,
                        label: `${getExecutiveFullName(authCtx.userIdentifier)} (me)`
                    }]
                    : []),
                ...hasAccessTo.map(e => ({
                    value: e,
                    label: getExecutiveFullName(e),
                }))
            ];

    const [activityData, setActivityData] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedExecutives, setSelectedExecutives] = useState([]);
    const [summaryType, setSummaryType] = useState({ value: '0', label: '0' });
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [isLoading, setIsLoading] = useState(false);

    // Cell selection state
    const [selectedCells, setSelectedCells] = useState(new Set());
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionStart, setSelectionStart] = useState(null);

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date('2025-01-01'),
            endDate: new Date(),
            key: 'selection',
        },
    ]);

    const handleSelect = (ranges) => {
        setDateRange([ranges.selection]);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker]);

    const summaryTypes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];

    const getRecruiterActivityAnalysis = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(BASE_URL_API + '/getRecruiterActivityAnalysis', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    dayInterval: summaryType.value,
                    startDate: dateRange[0].startDate,
                    endDate: dateRange[0].endDate,
                    executiveIDs: selectedExecutives.map(exec => exec.value),
                    executiveIdentifier: authCtx.userIdentifier
                }),
            });

            const data = await response.json();
            if (data.status === "success") {
                setActivityData(data?.response || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Filter data by selected executives
    const filteredData = useMemo(() => {
        if (selectedExecutives.length === 0) return activityData;
        const selectedIds = selectedExecutives.map(exec => exec.value);
        return activityData.filter(entry => selectedIds.includes(entry.executive_id));
    }, [activityData, selectedExecutives]);

    // Sort data
    const sortedData = useMemo(() => {
        const sorted = [...filteredData];
        sorted.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            // Handle numeric values
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
            }

            // Handle string values
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filteredData, sortConfig]);

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (filteredData.length === 0) return null;

        const totals = filteredData.reduce((acc, entry) => ({
            total_actions: acc.total_actions + parseInt(entry.total_actions || 0),
            total_contacted: acc.total_contacted + parseInt(entry.total_contacted || 0),
            total_could_not_had_conversation: acc.total_could_not_had_conversation + parseInt(entry.total_could_not_had_conversation || 0),
            total_had_conversation: acc.total_had_conversation + parseInt(entry.total_had_conversation || 0),
            total_profile_not_considered: acc.total_profile_not_considered + parseInt(entry.total_profile_not_considered || 0),
        }), {
            total_actions: 0,
            total_contacted: 0,
            total_could_not_had_conversation: 0,
            total_had_conversation: 0,
            total_profile_not_considered: 0,
        });

        const contactRate = totals.total_actions > 0
            ? ((totals.total_contacted / totals.total_actions) * 100).toFixed(2)
            : 0;

        const conversationRate = totals.total_contacted > 0
            ? ((totals.total_had_conversation / totals.total_contacted) * 100).toFixed(2)
            : 0;

        return { ...totals, contactRate, conversationRate };
    }, [filteredData]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '⇅';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    // Calculate efficiency score for each row
    const calculateEfficiency = (entry) => {
        const hadConversation = parseInt(entry.total_had_conversation || 0);
        const contacted = parseInt(entry.total_contacted || 0);
        const actions = parseInt(entry.total_actions || 0);
        return contacted > 0 ? ((hadConversation / contacted) * 100).toFixed(1) : 0;
    };

    const calculateInterestedRate = (entry) => {
        const profileNotInterested = parseInt(entry.total_profile_not_considered || 0);
        const hadConversation = parseInt(entry.total_had_conversation || 0);
        return hadConversation > 0 ? parseFloat(100 - ((profileNotInterested / hadConversation) * 100)?.toFixed(1)).toFixed(1) : 0;
    }

    // CELL SELECTION HANDLERS
    const handleCellMouseDown = (rowIndex, columnKey, event) => {
        // Only handle left click
        if (event.button !== 0) return;

        const cellId = `${rowIndex}-${columnKey}`;

        // If Ctrl/Cmd key is pressed, toggle individual cell
        if (event.ctrlKey || event.metaKey) {
            setSelectedCells(prev => {
                const newSet = new Set(prev);
                if (newSet.has(cellId)) {
                    newSet.delete(cellId);
                } else {
                    newSet.add(cellId);
                }
                return newSet;
            });
        } else {
            // Start new selection
            setSelectedCells(new Set([cellId]));
            setSelectionStart({ rowIndex, columnKey });
            setIsSelecting(true);
        }
    };

    const handleCellMouseEnter = (rowIndex, columnKey) => {
        if (!isSelecting || !selectionStart) return;

        const startRow = Math.min(selectionStart.rowIndex, rowIndex);
        const endRow = Math.max(selectionStart.rowIndex, rowIndex);

        // Get all column keys
        const columnKeys = getNumericColumnKeys();
        const startColIndex = columnKeys.indexOf(selectionStart.columnKey);
        const endColIndex = columnKeys.indexOf(columnKey);

        if (startColIndex === -1 || endColIndex === -1) return;

        const startCol = Math.min(startColIndex, endColIndex);
        const endCol = Math.max(startColIndex, endColIndex);

        // Create selection range
        const newSelection = new Set();
        for (let row = startRow; row <= endRow; row++) {
            for (let col = startCol; col <= endCol; col++) {
                newSelection.add(`${row}-${columnKeys[col]}`);
            }
        }

        setSelectedCells(newSelection);
    };

    const handleMouseUp = () => {
        setIsSelecting(false);
    };

    useEffect(() => {
        if (isSelecting) {
            document.addEventListener('mouseup', handleMouseUp);
            return () => document.removeEventListener('mouseup', handleMouseUp);
        }
    }, [isSelecting]);

    // Get numeric column keys (excluding date and executive_id)
    const getNumericColumnKeys = () => {
        return [
            'total_contacted',
            'total_could_not_had_conversation',
            'total_had_conversation',
            'total_profile_not_considered',
            'candidate_busy',
            'did_not_pick',
            'switch_off',
            'call_disconnected',
            'invalid_number',
            'no_incoming_available',
            'out_of_reach',
            'busy_another_call',
            'hanging_up_call',
            'other_issues'
        ];
    };

    // Calculate statistics for selected cells
    const selectionStatistics = useMemo(() => {
        if (selectedCells.size === 0) return null;

        const values = [];
        selectedCells.forEach(cellId => {
            const [rowIndex, columnKey] = cellId.split('-');
            const row = sortedData[parseInt(rowIndex)];
            if (row) {
                let value;
                if (columnKey === 'other_issues') {
                    value =
                        parseInt(row.someone_else_picked || 0) +
                        parseInt(row.call_dropped || 0) +
                        parseInt(row.wrong_person_number || 0) +
                        parseInt(row.wrong_number_lowercase || 0);
                } else {
                    value = parseFloat(row[columnKey]);
                }

                if (!isNaN(value)) {
                    values.push(value);
                }
            }
        });

        if (values.length === 0) return null;

        const sum = values.reduce((acc, val) => acc + val, 0);
        const average = sum / values.length;
        const count = values.length;
        const max = Math.max(...values);
        const min = Math.min(...values);

        return {
            sum: sum.toFixed(0),
            average: average.toFixed(2),
            count,
            max,
            min
        };
    }, [selectedCells, sortedData]);

    // Handle column header click (select entire column)
    const handleColumnHeaderClick = (columnKey, event) => {
        event.stopPropagation();

        // Check if it's a numeric column
        const numericColumns = getNumericColumnKeys();
        if (!numericColumns.includes(columnKey)) return;

        const newSelection = new Set();
        sortedData.forEach((_, rowIndex) => {
            newSelection.add(`${rowIndex}-${columnKey}`);
        });

        setSelectedCells(newSelection);
    };

    const isCellSelected = (rowIndex, columnKey) => {
        return selectedCells.has(`${rowIndex}-${columnKey}`);
    };

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.header}>
                <div className={styles.leftWrapper}>
                    <h1 className={styles.title}>RPA</h1>
                    <span className={styles.subtitle} >Recruiter Performance Analytics</span>
                </div>

                <div className={styles.rightWrapper}>
                    {/* DATE PICKER */}
                    <div className={styles.datePickerWrapper} ref={pickerRef}>
                        <div
                            className={styles.dateInput}
                            onClick={() => setShowDatePicker(prev => !prev)}
                        >
                            📅 {dateRange[0].startDate.toLocaleDateString()} — {dateRange[0].endDate.toLocaleDateString()}
                        </div>

                        {showDatePicker && (
                            <div className={styles.datePickerDropdown}>
                                <DateRangePicker
                                    ranges={dateRange}
                                    onChange={handleSelect}
                                    locale={enUS}
                                    maxDate={new Date()}
                                />
                            </div>
                        )}
                    </div>

                    {/* EXECUTIVE FILTER */}
                    <div className={styles.executiveFilter}>
                        <Select
                            options={optionsExecutive}
                            placeholder="Select Executives"
                            className={styles.selectBox}
                            isMulti
                            isSearchable
                            value={selectedExecutives}
                            onChange={setSelectedExecutives}
                        />
                    </div>

                    <div className={styles.parameterFilter}>
                        <Select
                            options={summaryTypes.map(type => ({
                                value: type,
                                label: type,
                            }))}
                            placeholder="Summary Type"
                            className={styles.selectBox}
                            isSearchable
                            value={summaryType}
                            onChange={setSummaryType}
                        />
                    </div>

                    <div>
                        <button
                            className={styles.fetchButton}
                            onClick={getRecruiterActivityAnalysis}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Loading...' : 'Fetch Data'}
                        </button>
                    </div>
                </div>
            </div>

            {/* SUMMARY CARDS */}
            {summaryStats && (
                <div className={styles.summaryCards}>
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>Total Actions</div>
                        <div className={styles.cardValue}>{summaryStats.total_actions.toLocaleString()}</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>Total Contacted</div>
                        <div className={styles.cardValue}>{summaryStats.total_contacted.toLocaleString()}</div>
                        <div className={styles.cardSubtext}>{summaryStats.contactRate}% contact rate</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>Had Conversation</div>
                        <div className={styles.cardValue}>{summaryStats.total_had_conversation.toLocaleString()}</div>
                        <div className={styles.cardSubtext}>{summaryStats.conversationRate}% conversion</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>Could Not Connect</div>
                        <div className={styles.cardValue}>{summaryStats.total_could_not_had_conversation.toLocaleString()}</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardLabel}>Not Considered</div>
                        <div className={styles.cardValue}>{summaryStats.total_profile_not_considered.toLocaleString()}</div>
                    </div>
                </div>
            )}

            {/* SELECTION STATISTICS BAR */}
            {selectionStatistics && (
                <div className={styles.selectionStats}>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Count:</span>
                        <span className={styles.statValue}>{selectionStatistics.count}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Sum:</span>
                        <span className={styles.statValue}>{selectionStatistics.sum}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Average:</span>
                        <span className={styles.statValue}>{selectionStatistics.average}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Max:</span>
                        <span className={styles.statValue}>{selectionStatistics.max}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Min:</span>
                        <span className={styles.statValue}>{selectionStatistics.min}</span>
                    </div>
                    <button
                        className={styles.clearSelection}
                        onClick={() => setSelectedCells(new Set())}
                    >
                        Clear Selection
                    </button>
                </div>
            )}

            <div className={styles.content}>
                {isLoading ? (
                    <div className={styles.loadingState}>Loading data...</div>
                ) : sortedData.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No data available. Click "Fetch Data" to load analytics.</p>
                    </div>
                ) : (
                    <div className={styles.activityTableWrapper}>
                        <div className={styles.tableHeader}>
                            <span className={styles.recordCount}>
                                Showing {sortedData.length} record(s)
                            </span>
                            <span className={styles.selectionHint}>
                                💡 Click & drag to select cells | Double-click column header to select entire column | Ctrl+Click for multi-select
                            </span>
                        </div>

                        <table className={styles.activityTable}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('date')} className={styles.sortable}>
                                        Date {getSortIcon('date')}
                                    </th>
                                    <th onClick={() => handleSort('executive_id')} className={styles.sortable}>
                                        Executive {getSortIcon('executive_id')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('total_contacted')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('total_contacted', e)}
                                        className={styles.sortable}
                                    >
                                        Contacted {getSortIcon('total_contacted')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('total_could_not_had_conversation')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('total_could_not_had_conversation', e)}
                                        className={styles.sortable}
                                    >
                                        Not Conversation {getSortIcon('total_could_not_had_conversation')}
                                    </th>
                                    <th className={styles.subHeader}>Conversation Rate %</th>
                                    <th
                                        onClick={() => handleSort('total_had_conversation')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('total_had_conversation', e)}
                                        className={styles.sortable}
                                    >
                                        Had Conversation {getSortIcon('total_had_conversation')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('total_profile_not_considered')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('total_profile_not_considered', e)}
                                        className={styles.sortable}
                                    >
                                        Not Considered {getSortIcon('total_profile_not_considered')}
                                    </th>
                                    <th className={styles.subHeader}>Interested %</th>
                                    <th
                                        onClick={() => handleSort('candidate_busy')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('candidate_busy', e)}
                                        className={styles.sortable}
                                    >
                                        Candidate Busy {getSortIcon('candidate_busy')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('did_not_pick')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('did_not_pick', e)}
                                        className={styles.sortable}
                                    >
                                        Did Not Pick {getSortIcon('did_not_pick')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('switch_off')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('switch_off', e)}
                                        className={styles.sortable}
                                    >
                                        Switch Off {getSortIcon('switch_off')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('call_disconnected')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('call_disconnected', e)}
                                        className={styles.sortable}
                                    >
                                        Call Disconnected {getSortIcon('call_disconnected')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('invalid_number')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('invalid_number', e)}
                                        className={styles.sortable}
                                    >
                                        Invalid Number {getSortIcon('invalid_number')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('no_incoming_available')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('no_incoming_available', e)}
                                        className={styles.sortable}
                                    >
                                        No Incoming {getSortIcon('no_incoming_available')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('out_of_reach')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('out_of_reach', e)}
                                        className={styles.sortable}
                                    >
                                        Out of Reach {getSortIcon('out_of_reach')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('busy_another_call')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('busy_another_call', e)}
                                        className={styles.sortable}
                                    >
                                        Busy Another Call {getSortIcon('busy_another_call')}
                                    </th>
                                    <th
                                        onClick={() => handleSort('hanging_up_call')}
                                        onDoubleClick={(e) => handleColumnHeaderClick('hanging_up_call', e)}
                                        className={styles.sortable}
                                    >
                                        Hanging Up Call {getSortIcon('hanging_up_call')}
                                    </th>
                                    <th onDoubleClick={(e) => handleColumnHeaderClick('other_issues', e)}>
                                        Other Issues
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((entry, index) => {
                                    const efficiency = calculateEfficiency(entry);
                                    const interested = calculateInterestedRate(entry);
                                    const otherIssues = parseInt(entry.hanging_up_call || 0) +
                                        parseInt(entry.someone_else_picked || 0) +
                                        parseInt(entry.call_dropped || 0) +
                                        parseInt(entry.wrong_person_number || 0) +
                                        parseInt(entry.wrong_number_lowercase || 0);

                                    return (
                                        <tr key={index} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                                            <td className={styles.dateCell}>{entry.date}</td>
                                            <td className={styles.executiveCell}>
                                                {getExecutiveFullName(entry.executive_id) || entry.executive_id}
                                            </td>
                                            <td
                                                className={`${styles.highlightCell} ${isCellSelected(index, 'total_contacted') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'total_contacted', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'total_contacted')}
                                            >
                                                {entry.total_contacted}
                                            </td>
                                            <td
                                                className={`${styles.failCell} ${isCellSelected(index, 'total_could_not_had_conversation') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'total_could_not_had_conversation', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'total_could_not_had_conversation')}
                                            >
                                                {entry.total_could_not_had_conversation}
                                            </td>
                                            <td className={styles.efficiencyCell}>
                                                <span className={
                                                    efficiency >= 25 ? styles.highEfficiency :
                                                        efficiency >= 15 ? styles.mediumEfficiency :
                                                            styles.lowEfficiency
                                                }>
                                                    {efficiency}%
                                                </span>
                                            </td>
                                            <td
                                                className={`${styles.successCell} ${isCellSelected(index, 'total_had_conversation') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'total_had_conversation', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'total_had_conversation')}
                                            >
                                                {entry.total_had_conversation}
                                            </td>
                                            <td
                                                className={`${styles.rejectedCell} ${isCellSelected(index, 'total_profile_not_considered') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'total_profile_not_considered', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'total_profile_not_considered')}
                                            >
                                                {entry.total_profile_not_considered}
                                            </td>
                                            <td className={styles.efficiencyCell}>
                                                <span className={
                                                    interested >= 25 ? styles.highEfficiency :
                                                        interested >= 15 ? styles.mediumEfficiency :
                                                            styles.lowEfficiency
                                                }>
                                                    {interested}%
                                                </span>
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'candidate_busy') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'candidate_busy', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'candidate_busy')}
                                            >
                                                {entry.candidate_busy}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'did_not_pick') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'did_not_pick', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'did_not_pick')}
                                            >
                                                {entry.did_not_pick}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'switch_off') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'switch_off', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'switch_off')}
                                            >
                                                {entry.switch_off}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'call_disconnected') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'call_disconnected', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'call_disconnected')}
                                            >
                                                {entry.call_disconnected}
                                            </td>
                                            <td
                                                className={`${styles.warningCell} ${isCellSelected(index, 'invalid_number') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'invalid_number', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'invalid_number')}
                                            >
                                                {entry.invalid_number}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'no_incoming_available') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'no_incoming_available', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'no_incoming_available')}
                                            >
                                                {entry.no_incoming_available}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'out_of_reach') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'out_of_reach', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'out_of_reach')}
                                            >
                                                {entry.out_of_reach}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'busy_another_call') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'busy_another_call', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'busy_another_call')}
                                            >
                                                {entry.busy_another_call}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'hanging_up_call') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'hanging_up_call', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'hanging_up_call')}
                                            >
                                                {entry.hanging_up_call}
                                            </td>
                                            <td
                                                className={`${styles.numericCell} ${isCellSelected(index, 'other_issues') ? styles.selectedCell : ''}`}
                                                onMouseDown={(e) => handleCellMouseDown(index, 'other_issues', e)}
                                                onMouseEnter={() => handleCellMouseEnter(index, 'other_issues')}
                                            >
                                                {otherIssues}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecruiterAnalytics;